import React, { useEffect, useState, useMemo } from 'react';
import { Layout, Menu, Dropdown } from 'antd'; 
import './App.css';
import { Navigate, Route, Routes, useNavigate, useLocation } from 'react-router-dom';  
import { observer } from 'mobx-react-lite'; 
import { globalStore } from './GlobalStore';
import LoginModal from './components/LoginModal/LoginModal';
import Home from './views/pages/Home';
import OrderContract from './views/pages/OrderContract';
import OrderContractDetail from './views/pages/OrderContractDetail';
import DownloadApp from './views/pages/DownloadApp';
import RemoteDelivery from './views/pages/RemoteDelivery';
import Footer from './components/Footer';
import SoftwareDev from './views/pages/SoftwareDev';
import UserProtocol from './views/pages/UserProtocol';
import PrivateRules from './views/pages/PrivateRules';
import PersonalInfoProtect from './views/pages/PersonalInfoProtect';
import TradingRulesPage from './trading-rules/TradingRulesPage';
import UserAgreementPage from './user-agreement/UserAgreementPage';
import PolicyStatementPage from './policy-statement/PolicyStatementPage';
import PersonalInformationProtectPage from './personal-information-protect/PersonalInformationProtectPage';
import MessagePage from './message/MessagePage';
import PersonalModal from './profile/personal/PersonalModal';
import HelloBuyerAppH5Page from './hello-buyer-app-h5/HelloBuyerAppH5Page';
import HelloSellerAppH5Page from './hello-seller-app-h5/HelloSellerAppH5Page';
import HelpCenterPage from './help-center/HelpCenterPage';
import { PAGE_INLINE_PADDING_PX } from './constants/pageLayout';
const { Header, Content } = Layout;
 
interface AppProps {
  onLoad?: () => void;
}

const App: React.FC<AppProps> = observer(({ onLoad })=>{
  const [isScrolled, setIsScrolled] = useState(false);
  const [personalModalVisible, setPersonalModalVisible] = useState(false);
  const location = useLocation();

  // 判断是否为规则页面（不显示Header和Footer）
  const isRulesPage = ['/h5/trading-rules', '/h5/user-agreement', '/h5/policy-statement', '/h5/personal-information-protect', '/h5/privacy-policy', '/webview/privacy-policy', '/webview/personal-information-protect', '/user-protocal', '/private-rules', '/personal-information-protect', '/privacy-policy', '/helloBuyerAppH5', '/helloSellerAppH5', '/help-center', '/h5/help-center'].includes(location.pathname);
  const isMessagePage = location.pathname === '/message';

  // 针对软件开发页的深色主题色（静态变量）
  const softwarePageColor = 'black';

  // 在软件开发页时，强制使用深色头部与深色配色方案
  const useLightHeaderScheme = (isScrolled || globalStore.forceWhiteHeader) && globalStore.selectedMenuKey !== 'software-dev';

  // 使用 useMemo 缓存导航按钮颜色
  const menuItemColors = useMemo(() => {
    const selectedKey = globalStore.selectedMenuKey;
    
    const getColor = (itemKey: string) => {
      const isSelected = selectedKey === itemKey;
      
      if (useLightHeaderScheme) {
        return isSelected ? "#000000" : "#777777";
      } else {
        return isSelected ? "#ffffff" : "#b1b1b1";
      }
    };

    return {
      home: getColor('home'),
      'daily-delivery': getColor('daily-delivery'),
      'software-dev': getColor('software-dev'),
      'order-contract': getColor('order-contract'),
      'sell-service': getColor('sell-service'),
      'download-app': getColor('download-app'),
      message: getColor('message'),
    };
  }, [useLightHeaderScheme, globalStore.selectedMenuKey]);

  useEffect(() => {
    onLoad?.();
  }, [onLoad]);

  useEffect(() => {
     globalStore.init();
  }, []);

  // 根据当前路径设置正确的菜单选中状态
  useEffect(() => {
    const path = location.pathname;
    let menuKey = 'home'; // 默认值
    
    if (path === '/' || path === '/home') {
      menuKey = 'home';
    } else if (path === '/remote-delivery') {
      menuKey = 'daily-delivery';
    } else if (path === '/software-dev') {
      menuKey = 'software-dev';
    } else if (path.startsWith('/order-contract')) {
      menuKey = 'order-contract';
    } else if (path === '/download-app') {
      menuKey = 'download-app';
    } else if (path === '/message') {
      menuKey = 'message';
    }
    
    if (globalStore.selectedMenuKey !== menuKey) {
      globalStore.selectedMenuKey = menuKey;
    }
  }, [location.pathname]);

  // 添加滚动监听
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      if (scrollTop > 200) {
        setIsScrolled(true);
      } else if (scrollTop < 20) {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigate = useNavigate();

  const handleMenuClick = (e: any) => {
    switch (e.key) {
      case 'home':
        globalStore.selectedMenuKey = e.key;
        navigate('/');
        break;
      case 'daily-delivery':
        globalStore.selectedMenuKey = e.key;
        navigate('/remote-delivery');
        break;
      case 'software-dev':
        globalStore.selectedMenuKey = e.key;
        navigate('/software-dev');
        break;
      case 'order-contract':
        if (!globalStore.isLoggedIn) {
          globalStore.loginModalVisible = true;
          return;
        }
        globalStore.selectedMenuKey = e.key;
        navigate('/order-contract');
        break;
      case 'message':
        if (!globalStore.isLoggedIn) {
          globalStore.loginModalVisible = true;
          return;
        }
        globalStore.selectedMenuKey = e.key;
        navigate('/message');
        break;
      case 'sell-service':
        window.open('https://seller.yuanling.com', '_blank');
        break;
     
      case 'download-app':
        globalStore.selectedMenuKey = e.key;
        navigate('/download-app');
        break;
      default:
        break;
    }
  };

  const handleNicknameClick = () => {
    if (!globalStore.isLoggedIn) {
      globalStore.loginModalVisible = true;
      return;
    }
    setPersonalModalVisible(true);
  };

  const logout = () => {
    globalStore.logout();
  }

  const menu = {
    items: [
      {
        key: 'profile',
        label: '个人资料',
        onClick: handleNicknameClick,
      },
      {
        key: 'logout',
        label: '退出登录',
        onClick: logout,
      },
    ],
  };

  return (
    <Layout className='web-app' style={{background:"#ffffff", minHeight: "100vh"}}>
 
      <Layout className='web-app' style={{ background:"#ffffff", minHeight: "100vh" }}>
        {!isRulesPage && <Header
          className="header"
          style={{
            backgroundColor: useLightHeaderScheme ? '#ffffff' : (globalStore.selectedMenuKey === 'software-dev' ? softwarePageColor : '#013510'),
            backdropFilter: 'saturate(1.2) blur(8px)',
            borderBottom: useLightHeaderScheme ? '0.5px solid #e5e5e5' : `0.5px solid ${globalStore.selectedMenuKey === 'software-dev' ? softwarePageColor : '#013510'}`,
            position: 'fixed',   // 关键：Header 悬浮在顶部，视频在其下方"贯穿全屏"
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            height: 60,
            lineHeight: '60px',
            display: 'flex',
            alignItems: 'center',
            transition: 'all 0.3s ease',
            paddingInline: PAGE_INLINE_PADDING_PX,
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              height: '100%',
              ['--menu-color-selected' as string]: useLightHeaderScheme ? '#000000' : '#ffffff',
              ['--menu-color-unselected' as string]: useLightHeaderScheme ? '#777777' : '#b1b1b1',
            }}
          >
            {/* Logo 和 Slogan */}
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div 
                style={{ marginRight: '10px', display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                onClick={() => navigate('/')}
              >
                <img 
                  src={useLightHeaderScheme ? "/logo_for_white.svg" : "/logo_for_deep_color.svg"} 
                  alt="Logo" 
                  style={{ height: '40px' }} 
                />
              </div>
              <div style={{ 
                fontSize: '16px', 
                fontWeight: 'light',
                color: useLightHeaderScheme ? "#777777" : "#ffffff"
              }}>
                圆领·加速工作向远程技能交易转变
              </div>
            </div>

            {/* 导航菜单 */}
            <Menu
              theme="light"
              mode="horizontal"
              selectedKeys={[globalStore.selectedMenuKey as string]}
              items={[
                {
                  key: 'home',
                  label: '首页',
                  style: { color: menuItemColors.home, fontWeight: '300' }
                },
                ...(!globalStore.staticMode && globalStore.enableFullAccess ? [{
                  key: 'message',
                  label: '消息',
                  style: { color: menuItemColors.message, fontWeight: '300' }
                }, {
                  key: 'order-contract',
                  label: '订单合同',
                  style: { color: menuItemColors['order-contract'], fontWeight: '300' }
                }] : []),
                {
                  key: 'daily-delivery',
                  label: '日交付可视化',
                  style: { color: menuItemColors['daily-delivery'], fontWeight: '300' }
                },
                {
                  key: 'software-dev',
                  label: '悟空·软件开发',
                  style: { color: menuItemColors['software-dev'], fontWeight: '300' }
                },
                {
                  key: 'download-app',
                  label: '下载APP',
                  style: { color: menuItemColors['download-app'], fontWeight: '300' }
                },
                {
                  key: 'sell-service',
                  label: '我是卖家',
                  style: { color: menuItemColors['sell-service'], fontWeight: '300' }
                },
              ]}
              onClick={handleMenuClick}
              style={{ 
                flex: 1, 
                justifyContent: 'flex-end', 
                marginRight:'50px', 
                height:'50px', 
                lineHeight:'50px', 
                background:'transparent',
                border: 'none'
              }}
              className="custom-menu"
            />

            {/* 用户昵称或登录注册按钮，staticMode 下隐藏 */}
            {!globalStore.staticMode && (globalStore.isLoggedIn ? (
              <Dropdown menu={menu} trigger={['click']}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  height: '100%', 
                  color: useLightHeaderScheme ? "#777777" : "#ffffff"
                }}>
                  <span>你好，</span>
                  <span style={{ cursor: 'pointer', fontWeight: 500, marginRight: '8px' }}>
                    {globalStore.currentBuyer?.nickname}
                  </span>
                  <img 
                    src={globalStore.currentBuyer?.avatarUrl || '/logo.svg'} 
                    alt="用户头像" 
                    style={{ 
                      width: '32px', 
                      height: '32px', 
                      borderRadius: '50%', 
                      objectFit: 'cover',
                      cursor: 'pointer'
                    }} 
                  />
                </div>
              </Dropdown>
            ) : (
              <span 
                onClick={() => { globalStore.loginModalVisible = true }} 
                style={{ 
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  height: '100%',
                  color: useLightHeaderScheme ? "#777777" : "#ffffff"
                }}
              >
                登录/注册
              </span>
            ))}
          </div>
        </Header>}

        {/* 注意：Content 不再包一层 content-container，且无 padding/margin */}
        <Content style={{ padding: 0 }}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/home" element={<Navigate to="/" />} />
            <Route path="/order-contract" element={globalStore.enableFullAccess ? <OrderContract /> : <Navigate to="/" replace />} />
            <Route path="/order-contract/:orderId" element={globalStore.enableFullAccess ? <OrderContractDetail /> : <Navigate to="/" replace />} />
            <Route path="/download-app" element={<DownloadApp />} />
            <Route path="/remote-delivery" element={<RemoteDelivery />} />
            <Route path="/software-dev" element={<SoftwareDev />} />
            <Route path="/h5/trading-rules" element={<TradingRulesPage />} />
            <Route path="/h5/user-agreement" element={<UserAgreementPage />} />
            <Route path="/h5/policy-statement" element={<PolicyStatementPage />} />
            <Route path="/h5/personal-information-protect" element={<PersonalInformationProtectPage />} />
            <Route path="/h5/privacy-policy" element={<PersonalInformationProtectPage />} />
            <Route path="/webview/privacy-policy" element={<PersonalInformationProtectPage />} />
            <Route path="/webview/personal-information-protect" element={<PersonalInformationProtectPage />} />
            <Route path="/privacy-policy" element={<PersonalInformationProtectPage />} />
            <Route path="/user-protocal" element={<UserProtocol />} />
            <Route path="/private-rules" element={<PrivateRules />} />
            <Route path="/personal-information-protect" element={<PersonalInfoProtect />} />
            <Route path="/message" element={globalStore.enableFullAccess ? <MessagePage /> : <Navigate to="/" replace />} />
            <Route path="/helloBuyerAppH5" element={<HelloBuyerAppH5Page />} />
            <Route path="/helloSellerAppH5" element={<HelloSellerAppH5Page />} />
            <Route path="/help-center" element={<HelpCenterPage />} />
            <Route path="/h5/help-center" element={<HelpCenterPage />} />
          </Routes>
        </Content>

        {!isRulesPage && !isMessagePage && <Footer />}

        {!globalStore.staticMode && (
          <>
            <LoginModal
              visible={globalStore.loginModalVisible}
              onClose={() => { globalStore.loginModalVisible = false }}
            />
            <PersonalModal
              visible={personalModalVisible}
              onClose={() => setPersonalModalVisible(false)}
            />
          </>
        )}
      </Layout>
    </Layout>
  );
});

export default App;